import {WORLDS} from './levels.js';
import {fallIntensity} from './collapse.js';
export class Sound {
  constructor(){this.enabled=true;this.world=0;this.beat=0;this.next=0;this.started=false;}
  async start(){
    if(!this.ctx){
      this.ctx=new(window.AudioContext||window.webkitAudioContext)();
      this.master=this.ctx.createGain();this.master.gain.value=.48;
      this.bed=this.ctx.createGain();this.bed.connect(this.master);
      this.compressor=this.ctx.createDynamicsCompressor();this.compressor.threshold.value=-14;this.compressor.ratio.value=5;
      this.master.connect(this.compressor);this.compressor.connect(this.ctx.destination);
      this.capture=this.ctx.createMediaStreamDestination();this.compressor.connect(this.capture);
      const buffer=this.ctx.createBuffer(1,this.ctx.sampleRate*3,this.ctx.sampleRate),data=buffer.getChannelData(0);
      let sample=0;for(let i=0;i<data.length;i++){sample=(sample+(Math.random()*2-1)*.025)/1.02;data[i]=sample*5;}
      this.noiseBuffer=buffer;
      // Original dry timber fracture: a fast broadband attack and irregular
      // splinter ticks, followed by a short, lower wood resonance.
      this.snapBuffer=this.ctx.createBuffer(1,Math.ceil(this.ctx.sampleRate*.65),this.ctx.sampleRate);
      const snap=this.snapBuffer.getChannelData(0);let seed=19777,wood=0;
      const ticks=[[.012,.8],[.027,.48],[.061,.31],[.109,.22],[.177,.12],[.29,.065]];
      for(let i=0;i<snap.length;i++){
        const t=i/this.ctx.sampleRate;seed=(Math.imul(seed,1664525)+1013904223)>>>0;
        const white=seed/2147483648-1;wood=wood*.87+white*.13;
        let envelope=Math.exp(-t*125);
        for(const [at,level] of ticks)if(t>=at)envelope+=level*Math.exp(-(t-at)*170);
        snap[i]=(white*envelope*.86+wood*Math.exp(-t*12)*.55)*Math.min(1,t/.0005);
      }
      const wind=this.ctx.createBufferSource();wind.buffer=buffer;wind.loop=true;
      const filter=this.ctx.createBiquadFilter();filter.type='lowpass';filter.frequency.value=450;
      this.windGain=this.ctx.createGain();this.windGain.gain.value=.015;wind.connect(filter).connect(this.windGain).connect(this.bed);wind.start();
      const fall=this.ctx.createBufferSource();fall.buffer=buffer;fall.loop=true;
      this.fallFilter=this.ctx.createBiquadFilter();this.fallFilter.type='bandpass';this.fallFilter.Q.value=.65;
      this.fallGain=this.ctx.createGain();this.fallGain.gain.value=0;
      fall.connect(this.fallFilter).connect(this.fallGain).connect(this.master);fall.start();
      this.next=this.ctx.currentTime+.1;
    }
    await this.ctx.resume();this.started=true;this.setEnabled(this.enabled);
  }
  setEnabled(value){this.enabled=value;if(this.ctx)this.master.gain.setTargetAtTime(value?.48:0,this.ctx.currentTime,.08);}
  setWorld(id){if(this.world===id)return;this.world=id;this.beat=0;if(this.ctx)this.next=this.ctx.currentTime+.1;}
  resetCollapse(){
    this.lastSnap=null;this.lastSplinter=null;this.lastRattle=null;this.fallLevel=0;
    if(!this.ctx)return;const t=this.ctx.currentTime;
    this.bed.gain.cancelScheduledValues(t);this.bed.gain.setTargetAtTime(1,t,.035);
    this.fallGain.gain.cancelScheduledValues(t);this.fallGain.gain.setTargetAtTime(0,t,.025);
  }
  tone(midi,when,duration=.5,volume=.08,type='sine',bus=this.master){
    if(!this.ctx)return;const o=this.ctx.createOscillator(),g=this.ctx.createGain();
    o.type=type;o.frequency.value=440*Math.pow(2,(midi-69)/12);
    g.gain.setValueAtTime(0,when);g.gain.linearRampToValueAtTime(volume,when+.008);g.gain.exponentialRampToValueAtTime(.0001,when+duration);
    o.connect(g).connect(bus);o.start(when);o.stop(when+duration+.03);
  }
  noise(when,duration,volume,frequency,bus=this.master){
    const s=this.ctx.createBufferSource();s.buffer=this.noiseBuffer;
    const f=this.ctx.createBiquadFilter();f.type='bandpass';f.frequency.value=frequency;f.Q.value=1;
    const g=this.ctx.createGain();g.gain.setValueAtTime(volume,when);g.gain.exponentialRampToValueAtTime(.0001,when+duration);
    s.connect(f).connect(g).connect(bus);s.start(when);s.stop(when+duration);
  }
  snap(event,t){
    // One lead crack per short cascade; additional failing bolts stay quieter.
    const lead=t-(this.lastSnap??-Infinity)>.42;
    if(!lead){if(t-(this.lastSplinter??-Infinity)>.045){this.noise(t,.07,.18,1800);this.lastSplinter=t;}return;}
    this.lastSnap=t;
    const source=this.ctx.createBufferSource(),gain=this.ctx.createGain();source.buffer=this.snapBuffer;
    gain.gain.value=1.25+Math.min(.15,Math.max(0,(event.severity||1)-1)*.15);
    source.connect(gain).connect(this.master);source.start(t);
    // Small fast pitch drops add the frame's weight and a recoiling steel collar.
    for(const [from,to,volume,duration] of [[145,42,.3,.36],[2250,620,.12,.17],[310,76,.15,.26]]){
      const o=this.ctx.createOscillator(),g=this.ctx.createGain();o.type='triangle';
      o.frequency.setValueAtTime(from,t);o.frequency.exponentialRampToValueAtTime(to,t+duration);
      g.gain.setValueAtTime(0,t);g.gain.linearRampToValueAtTime(volume,t+.001);g.gain.exponentialRampToValueAtTime(.0001,t+duration);
      o.connect(g).connect(this.master);o.start(t);o.stop(t+duration+.01);
    }
    // A quiet, short worksite reflection gives the crack space without a long
    // synthetic explosion tail. The next hammer strike stays immediately usable.
    const echo=this.ctx.createBufferSource(),echoGain=this.ctx.createGain();echo.buffer=this.snapBuffer;
    echoGain.gain.value=.11;echo.connect(echoGain).connect(this.master);echo.start(t+.105);
    const bed=this.bed.gain;bed.cancelScheduledValues(t);bed.setValueAtTime(bed.value,t);bed.linearRampToValueAtTime(.04,t+.004);bed.setValueAtTime(.04,t+.24);bed.exponentialRampToValueAtTime(1,t+.95);
  }
  tick(game,playing){
    if(!this.ctx||this.ctx.state!=='running')return;
    const now=this.ctx.currentTime,theme=WORLDS[this.world];
    this.fallLevel=this.enabled&&playing?fallIntensity(game):0;
    this.fallGain.gain.setTargetAtTime(this.fallLevel*.65,now,.045);
    this.fallFilter.frequency.setTargetAtTime(550+this.fallLevel*1250,now,.08);
    if(this.fallLevel>.2&&now-(this.lastSnap??-Infinity)>.1&&now-(this.lastRattle??-Infinity)>.18){
      this.lastRattle=now;this.noise(now,.12,.13+this.fallLevel*.19,1800);this.tone(43+this.fallLevel*8,now,.16,.045,'triangle');
    }
    this.windGain.gain.setTargetAtTime(.018+Math.abs(game?.wind||0)*.06,now,.2);
    if(this.next<now-.25)this.next=now+.025;
    while(this.next<now+.13){
      const i=this.beat%16,note=theme.notes[Math.floor(i/2)];
      if(i%2===0)this.tone(note+12,this.next,.8,playing?.054:.037,'sine',this.bed);
      if(i%4===0)this.tone(note-12,this.next,1.6,.11,'sine',this.bed);
      if(playing&&i%2===1)this.noise(this.next,.045,.19,1800,this.bed);
      if(i===0){this.tone(74,this.next,1.4,.036,'sine',this.bed);this.tone(77,this.next+.18,1.5,.028,'sine',this.bed);this.tone(81,this.next+.38,1.8,.023,'sine',this.bed);}
      this.next+=60/theme.bpm/2;this.beat++;
    }
    if(playing&&game){
      const stress=game.connections.reduce((n,c)=>Math.max(n,c.broken?0:c.stress),0);
      if(stress>.55&&now-(this.lastCreak||0)>.45+(1-Math.min(stress,1))){
        this.lastCreak=now;this.tone(35+stress*5,now,.3,.065,'triangle');this.noise(now,.2,.13,650);
      }
    }
  }
  event(event){
    if(!this.ctx||!this.enabled)return;const t=this.ctx.currentTime;
    if(event.type==='build'){this.tone(48+(this.beat%3)*3,t,.085,.17,'triangle');this.noise(t,.04,.32,2200);}
    if(event.type==='break')this.snap(event,t);
    if(event.type==='impact'&&t-(this.lastImpact||0)>.1){this.lastImpact=t;this.noise(t,.28,.18+event.power*.03,210);if(event.power>5)this.tone(32,t,.25,.1,'triangle');}
    if(event.type==='brace'){[55,62].forEach((n,i)=>this.tone(n,t+i*.08,.2,.12,'triangle'));}
    if(event.type==='fragment'){[74,77,81].forEach((n,i)=>this.tone(n,t+i*.09,1.1,.12));}
    if(event.type==='win'){[62,69,74,77].forEach((n,i)=>this.tone(n,t+i*.13,1.8,.1));}
    if(event.type==='lose'){this.tone(38,t,.9,.13,'triangle');}
  }
}
